import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import { IMG } from "../data/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_COVERS = [IMG.footwear, IMG.development, IMG.leather, IMG.textiles];

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function Journal() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/journal`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <section data-testid="journal-hero" className="bg-ivory pt-36 md:pt-48">
        <div className="mx-auto max-w-7xl px-6 pb-16 md:px-12 md:pb-20">
          <Reveal>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-500">The Journal</p>
          </Reveal>
          <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight text-ink md:text-7xl">
            <MaskedLine delay={0.2}>Notes from the</MaskedLine>
            <MaskedLine delay={0.32}>
              <span className="italic text-terra">sourcing floor.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
              What we know about finding factories, developing products and protecting quality in India —
              written for brands doing it for real.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="journal-list" className="bg-ivory pb-28 md:pb-40">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="border-t border-stone-200">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={0.05 * i}>
                <Link
                  to={`/journal/${p.slug}`}
                  data-testid={`journal-card-${p.slug}`}
                  className="group grid items-center gap-8 border-b border-stone-200 py-12 transition-colors duration-500 hover:bg-stone-100 md:grid-cols-12 md:py-16"
                >
                  <div className="overflow-hidden md:col-span-4">
                    <img
                      src={p.image || FALLBACK_COVERS[i % FALLBACK_COVERS.length]}
                      alt={p.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                    />
                  </div>
                  <div className="md:col-span-7 md:pl-6">
                    <p className="font-label text-[10px] font-semibold uppercase tracking-[0.26em] text-terra">
                      {p.category} <span className="text-stone-400">· {formatDate(p.date)} · {p.read_time}</span>
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-2xl leading-snug tracking-tight text-ink transition-colors duration-300 group-hover:text-terra md:text-4xl">
                      {p.title}
                    </h2>
                    <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-600 md:text-base">{p.excerpt}</p>
                  </div>
                  <div className="hidden md:col-span-1 md:flex md:justify-end">
                    <ArrowUpRight className="h-6 w-6 text-stone-300 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-terra" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          {posts.length === 0 && (
            <p className="py-20 font-serif text-2xl italic text-stone-400" data-testid="journal-empty">
              New notes are on their way.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
